// =============================================================================
// ui/shell/UnitPicker.tsx — the global $ / points / ticks / R control.
// -----------------------------------------------------------------------------
// One control, in the toolbar, setting the unit every tab reads — beside the
// range picker and for the same reason: a per-tab unit is how two screens come
// to disagree about what a number means.
//
// ⚠ STRUCTURE, NOT THE DESIGN SYSTEM, exactly as `RangePicker` says of itself.
// Beta uses a custom dropdown that borrows the date picker's CSS; this is a
// native `<select>` with the same four options and the same semantics. Building
// a bespoke menu by eye now would be redone against the real tokens in the
// visual parity pass.
//
// ── 🚨 THE ONE THING IT MUST NOT DO IS FAIL SILENTLY ──
//
// The unit lives in a shared JSONB blob, and `usePnlUnit` REFUSES to write while
// preferences are unread rather than risk clobbering the blob. A refusal has to
// be visible: without it the select would snap back to its previous value with
// no explanation, which reads as a broken control. So the control is disabled
// while it cannot write, and it says why.
//
// ── ⚠ WHY IT SHOWS THAT IT IS NOT IN DOLLARS ──
//
// Dollars is the default and by far the most common state, so a trader glancing
// at a screen full of "+82.50" assumes dollars unless something says otherwise.
// Beta marks this by highlighting the collapsed button whenever the unit is not
// `usd`; the same signal is carried here by `data-nondefault`, which the CSS
// tints. The alternative — trusting the reader to notice a small select — is how
// somebody reads points as dollars and thinks they made 82 dollars on a trade
// that made 82 points, which on MES is $410.
// =============================================================================

import { usePnlUnit } from '../state/usePnlUnit';
import { PNL_UNITS, PNL_UNIT_LABELS, isPnlUnit } from '../../selectors/pnlUnit';

export function UnitPicker() {
  const { unit, canWrite, busy, error, setUnit } = usePnlUnit();

  return (
    <div
      className="unit-picker"
      data-testid="unit-picker"
      // ⚠ Not `unit !== 'usd'` inline in a class string: the CSS hook and the
      // test both read this attribute, so there is one expression of "the app is
      // not showing dollars" rather than two that can disagree.
      data-nondefault={unit === 'usd' ? undefined : 'true'}
    >
      <label className="range-picker-label" htmlFor="unit-picker-select">UNIT</label>
      <select
        id="unit-picker-select"
        value={unit}
        disabled={!canWrite || busy}
        title={canWrite
          ? 'Show every P&L figure in this unit'
          : 'Your settings haven’t loaded yet'}
        onChange={(e) => {
          const next = e.target.value;
          // ⚠ Validated even though the options are ours. The stored value is
          // validated on the way in too — this is the same boundary, and a
          // browser extension or an autofill can put anything in a select.
          if (isPnlUnit(next)) void setUnit(next);
        }}
      >
        {PNL_UNITS.map((u) => (
          <option key={u} value={u}>{PNL_UNIT_LABELS[u]}</option>
        ))}
      </select>
      {/* ⚠ Rendered, not swallowed. See the header — a refused write that looks
          like nothing happened is worse than an error message. */}
      {error ? <span className="unit-picker-error">{error}</span> : null}
    </div>
  );
}
